(() => {
  "use strict";

  const FORM_ID = "project-review";
  const ATTRIBUTION_STORAGE_KEY = "ecpr:project-review:v3:attribution";
  const DRAFT_STORAGE_KEY = "ecpr:project-review:v3:draft";
  const PENDING_STORAGE_KEY = "ecpr:project-review:v3:pending-lead";
  const FIRED_STORAGE_KEY = "ecpr:project-review:v3:fired-leads";
  const CUSTOM_EVENT_NAME = "ecpr:project_review_generate_lead";
  const METADATA_FIELD_NAMES = {
    entry_point: "contact[internal-entry-point]",
    initial_landing_path: "contact[initial-landing-path]",
    external_referrer_host_path: "contact[external-referrer-host-and-path]",
    utm_source: "contact[utm-source]",
    utm_medium: "contact[utm-medium]",
    utm_campaign: "contact[utm-campaign]",
    utm_content: "contact[utm-content]",
    utm_term: "contact[utm-term]",
    gclid: "contact[gclid]",
    gbraid: "contact[gbraid]",
    wbraid: "contact[wbraid]",
    dclid: "contact[dclid]",
    fbclid: "contact[fbclid]",
    msclkid: "contact[msclkid]",
    ttclid: "contact[ttclid]",
  };

  const PARTS_DETAILS_REQUIRED_FOR = new Set([
    "I have specific parts in mind and want them reviewed before ordering",
    "I have already ordered the parts",
    "I already have the parts in hand",
  ]);

  const ANALYTICS_OPTIONS = {
    project_category: new Set([
      "Performance / Tuning",
      "Suspension / Brakes",
      "Aero / Body / Paint",
      "Diagnostics / Coding / Software",
      "Maintenance / Repair",
      "Parts Fitment / Sourcing",
      "Complete Build Planning",
      "Other",
    ]),
    vehicle_platform: new Set([
      "Porsche",
      "BMW",
      "Audi or Volkswagen",
      "MINI",
      "Toyota Supra",
      "Other European / Specialty Vehicle — reviewed selectively",
    ]),
    preferred_fulfillment: new Set([
      "Install at EuroCustomsPR in Rockledge",
      "Ship parts to me",
      "Ship parts to my installer",
      "Planning or advice only",
      "Not sure yet",
    ]),
    timeline: new Set([
      "As soon as possible",
      "Within 1 to 3 months",
      "Within 3 to 6 months",
      "Researching for later",
      "Not sure yet",
    ]),
    budget_band: new Set([
      "Below $1000",
      "$1000 to $2500",
      "$2500 to $5000",
      "$5000 to $10000",
      "$10000 to $25000",
      "Above $25000",
      "Not sure yet",
    ]),
    budget_scope: new Set([
      "Complete project: parts and labor",
      "Labor / installation / paint only",
      "Parts only",
      "Not sure—please advise",
    ]),
    entry_point: new Set(["homepage_process", "direct_project_review", "not_provided"]),
  };

  const ANALYTICS_FIELD_NAMES = {
    project_category: "contact[project-category]",
    vehicle_platform: "contact[vehicle-platform]",
    preferred_fulfillment: "contact[preferred-fulfillment]",
    timeline: "contact[preferred-timeline]",
    budget_band: "contact[estimated-project-budget]",
    budget_scope: "contact[what-does-this-budget-include]",
    entry_point: "contact[internal-entry-point]",
  };

  const normalize = (value) => {
    try {
      return String(value ?? "").normalize("NFKC");
    } catch (_error) {
      return String(value ?? "");
    }
  };

  const sanitizeText = (value, maxLength = 160) =>
    normalize(value)
      .replace(/[\u0000-\u001f\u007f]/g, "")
      .replace(/[^A-Za-z0-9 _.,:/+@~\-]/g, "")
      .trim()
      .slice(0, maxLength);

  const sanitizeToken = (value, maxLength = 180) =>
    normalize(value)
      .replace(/[^A-Za-z0-9._~:+/\-]/g, "")
      .slice(0, maxLength);

  const sanitizePath = (value) => {
    const path = normalize(value)
      .split(/[?#]/, 1)[0]
      .replace(/[^A-Za-z0-9._~/%\-]/g, "")
      .slice(0, 300);
    return path.startsWith("/") ? path : `/${path}`;
  };

  const readSession = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  };

  const writeSession = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const removeSession = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Storage can be unavailable in hardened browsing modes.
    }
  };

  const parseStoredObject = (value) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch (_error) {
      return null;
    }
  };

  const readAttribution = () => {
    const inMemory = window.ECPRProjectReviewAttribution;
    const stored = parseStoredObject(readSession(ATTRIBUTION_STORAGE_KEY));
    const source = inMemory && typeof inMemory === "object" && !Array.isArray(inMemory) ? inMemory : stored || {};
    const sanitized = {};

    Object.keys(METADATA_FIELD_NAMES).forEach((key) => {
      if (key === "entry_point") {
        const entryPoint = sanitizeToken(source[key], 80);
        sanitized[key] = ANALYTICS_OPTIONS.entry_point.has(entryPoint) ? entryPoint : "";
      } else if (key === "initial_landing_path") {
        sanitized[key] = source[key] ? sanitizePath(source[key]) : "";
      } else if (key === "external_referrer_host_path") {
        sanitized[key] = sanitizeText(source[key], 480);
      } else {
        const sanitizer = key.includes("clid") || key.includes("braid") ? sanitizeToken : sanitizeText;
        sanitized[key] = sanitizer(source[key], 180);
      }
    });

    return sanitized;
  };

  const attribution = readAttribution();

  const fieldByName = (form, name) => form.querySelector(`[name="${name}"]`);

  const editableFields = (form) =>
    Array.from(form.elements).filter(
      (field) =>
        field.name?.startsWith("contact[") &&
        field.type !== "hidden" &&
        field.type !== "submit" &&
        field.type !== "button"
    );

  const saveDraft = (form) => {
    const draft = {};
    editableFields(form).forEach((field) => {
      draft[field.name] = String(field.value || "").slice(0, 12000);
    });
    writeSession(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  };

  let draftSaveTimer;
  const scheduleDraftSave = (form) => {
    window.clearTimeout(draftSaveTimer);
    draftSaveTimer = window.setTimeout(() => saveDraft(form), 120);
  };

  const restoreDraft = (form) => {
    const draft = parseStoredObject(readSession(DRAFT_STORAGE_KEY));
    if (!draft) return;
    editableFields(form).forEach((field) => {
      if (typeof draft[field.name] === "string") field.value = draft[field.name];
    });
  };

  const populateMetadata = (form) => {
    Object.entries(METADATA_FIELD_NAMES).forEach(([key, name]) => {
      const field = fieldByName(form, name);
      if (!field) return;
      const value = attribution[key] || "";
      field.value = value;
      field.disabled = value === "";
    });
  };

  const fieldWrapper = (field) => field?.closest(".field") || null;

  const setFieldVisibility = (field, visible) => {
    const wrapper = fieldWrapper(field);
    if (!wrapper) return;
    wrapper.hidden = !visible;
    wrapper.setAttribute("aria-hidden", visible ? "false" : "true");
    if (!visible) field.removeAttribute("aria-invalid");
  };

  const setConditionalRequired = (field, required) => {
    if (!field) return;
    field.required = required;
    if (required) {
      field.setAttribute("aria-required", "true");
    } else {
      field.removeAttribute("aria-required");
    }

    const label = field.labels?.[0];
    if (!label) return;
    let marker = label.querySelector("[data-ecpr-conditional-required]");
    const staticMarker = label.querySelector('span[aria-hidden="true"]');
    if (required && !marker && !staticMarker) {
      marker = document.createElement("span");
      marker.setAttribute("aria-hidden", "true");
      marker.dataset.ecprConditionalRequired = "true";
      marker.textContent = " *";
      label.appendChild(marker);
    } else if (!required && marker) {
      marker.remove();
    }
  };

  const updateConditionalFields = (form) => {
    const partsStatus = fieldByName(form, "contact[parts-status-and-sourcing-preference]");
    const partsDetails = fieldByName(form, "contact[exact-parts-product-links-or-part-numbers]");
    const fulfillment = fieldByName(form, "contact[preferred-fulfillment]");
    const location = fieldByName(form, "contact[city-state-or-zip]");
    const responseMethod = fieldByName(form, "contact[preferred-response-method]");
    const consentNode = form.querySelector("#project-review-text-consent");
    const consent = consentNode?.matches(".flexi-richtext") ? consentNode : consentNode?.closest(".flexi-richtext");

    if (partsStatus && partsDetails) {
      const noParts = partsStatus.value === "No parts are involved";
      setFieldVisibility(partsDetails, !noParts);
      setConditionalRequired(partsDetails, !noParts && PARTS_DETAILS_REQUIRED_FOR.has(partsStatus.value));
    }

    if (fulfillment && location) {
      const hasSelection = fulfillment.value !== "";
      const isLocal = fulfillment.value === "Install at EuroCustomsPR in Rockledge";
      setFieldVisibility(location, !hasSelection || isLocal);
      setConditionalRequired(location, !hasSelection || isLocal);
    }

    if (responseMethod && consent) {
      const showConsent = responseMethod.value === "Text Message";
      consent.hidden = !showConsent;
      consent.classList.toggle("is-visible", showConsent);
      consent.setAttribute("aria-hidden", showConsent ? "false" : "true");
    }
  };

  const validatePhone = (form) => {
    const phone = fieldByName(form, "contact[phone]");
    if (!phone) return;
    const value = phone.value.trim();
    const digitCount = (value.match(/[0-9]/g) || []).length;
    const allowedCharacters = /^[0-9()+.\s-]+$/.test(value);
    if (value && (!allowedCharacters || digitCount < 10 || digitCount > 15)) {
      phone.setCustomValidity("Enter a valid phone number with 10 to 15 digits.");
    } else {
      phone.setCustomValidity("");
    }
  };

  const clearErrorSummary = (form) => {
    const summary = form.querySelector("#project-review-client-errors");
    if (summary) {
      summary.hidden = true;
      summary.replaceChildren();
    }
    Array.from(form.elements).forEach((field) => field.removeAttribute?.("aria-invalid"));
  };

  const fieldLabel = (field) =>
    (field.labels?.[0]?.textContent || field.name || "Field")
      .replace(/\s*\*\s*$/, "")
      .trim();

  const showErrorSummary = (form) => {
    const summary = form.querySelector("#project-review-client-errors");
    if (!summary) return;

    const invalidFields = Array.from(form.elements).filter(
      (field) => field.willValidate && !field.validity.valid && !field.closest("[hidden]")
    );

    const heading = document.createElement("h2");
    heading.className = "form-status-title";
    heading.textContent = "Please review the following fields";

    const list = document.createElement("ul");
    list.className = "form-status-list";
    list.setAttribute("role", "list");

    invalidFields.forEach((field) => {
      field.setAttribute("aria-invalid", "true");
      const item = document.createElement("li");
      const link = document.createElement("a");
      link.className = "link";
      link.href = `#${field.id}`;
      link.textContent = `${fieldLabel(field)}: ${field.validationMessage}`;
      link.addEventListener("click", () => field.focus());
      item.appendChild(link);
      list.appendChild(item);
    });

    summary.replaceChildren(heading, list);
    summary.hidden = false;
    summary.focus({ preventScroll: false });
  };

  const analyticsValue = (form, key) => {
    const field = fieldByName(form, ANALYTICS_FIELD_NAMES[key]);
    const rawValue = String(field?.value || "").slice(0, 100);
    if (ANALYTICS_OPTIONS[key].has(rawValue)) return rawValue;
    if (key === "entry_point" && rawValue) return "not_provided";
    return "not_provided";
  };

  const createSubmissionId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const savePendingLead = (form) => {
    const pending = {
      id: createSubmissionId(),
      params: {
        form_name: "project_review",
        form_version: "v3",
        project_category: analyticsValue(form, "project_category"),
        vehicle_platform: analyticsValue(form, "vehicle_platform"),
        preferred_fulfillment: analyticsValue(form, "preferred_fulfillment"),
        timeline: analyticsValue(form, "timeline"),
        budget_band: analyticsValue(form, "budget_band"),
        budget_scope: analyticsValue(form, "budget_scope"),
        entry_point: analyticsValue(form, "entry_point"),
      },
    };
    writeSession(PENDING_STORAGE_KEY, JSON.stringify(pending));
  };

  const firedLeadIds = () => {
    try {
      const parsed = JSON.parse(readSession(FIRED_STORAGE_KEY) || "[]");
      return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string").slice(-20) : [];
    } catch (_error) {
      return [];
    }
  };

  const markLeadFired = (id) => {
    const ids = firedLeadIds();
    if (!ids.includes(id)) ids.push(id);
    writeSession(FIRED_STORAGE_KEY, JSON.stringify(ids.slice(-20)));
    removeSession(PENDING_STORAGE_KEY);
  };

  const publishThroughExistingAnalytics = (params) => {
    if (typeof window.Shopify?.analytics?.publish === "function") {
      window.Shopify.analytics.publish(CUSTOM_EVENT_NAME, params);
      return "shopify_customer_events";
    }

    if (window.google_tag_manager && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: "generate_lead", ...params });
      return "google_tag_manager";
    }

    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", params);
      return "gtag";
    }

    return null;
  };

  const publishSuccessfulLead = (form, attempt = 0) => {
    const success = form.querySelector("[data-ecpr-project-review-success]");
    const pending = parseStoredObject(readSession(PENDING_STORAGE_KEY));
    if (!success || !pending?.id || !pending.params || firedLeadIds().includes(pending.id)) return;

    const channel = publishThroughExistingAnalytics(pending.params);
    if (!channel && attempt < 20) {
      window.setTimeout(() => publishSuccessfulLead(form, attempt + 1), 250);
      return;
    }
    if (!channel) return;

    markLeadFired(pending.id);
    document.documentElement.dataset.ecprLeadEvent = "published";
    window.dispatchEvent(
      new CustomEvent("ecpr:lead-event-dispatched", {
        detail: { channel, submission_id: pending.id },
      })
    );
  };

  const focusStatus = (form) => {
    const status = form.querySelector(".form-status-error:not([hidden]), [data-ecpr-project-review-success]");
    if (status) window.requestAnimationFrame(() => status.focus({ preventScroll: false }));
  };

  const bindAnchorFocus = (form) => {
    const heading = document.getElementById("project-review-form-heading");
    const anchor = document.querySelector('a[href="#project-review-form-heading"]');
    if (!heading || !anchor) return;
    anchor.addEventListener("click", () => {
      window.setTimeout(() => heading.focus({ preventScroll: true }), 0);
    });
  };

  const initializeProjectReview = () => {
    const form = document.getElementById(FORM_ID);
    if (!(form instanceof HTMLFormElement)) return;

    const success = form.querySelector("[data-ecpr-project-review-success]");
    if (success) {
      removeSession(DRAFT_STORAGE_KEY);
    } else {
      restoreDraft(form);
    }
    populateMetadata(form);
    updateConditionalFields(form);
    validatePhone(form);
    bindAnchorFocus(form);
    focusStatus(form);
    publishSuccessfulLead(form);

    form.noValidate = true;
    form.addEventListener("change", () => {
      updateConditionalFields(form);
      validatePhone(form);
      scheduleDraftSave(form);
    });

    form.addEventListener("input", (event) => {
      event.target?.removeAttribute?.("aria-invalid");
      if (event.target?.name === "contact[phone]") validatePhone(form);
      scheduleDraftSave(form);
    });

    form.addEventListener("submit", (event) => {
      updateConditionalFields(form);
      validatePhone(form);
      clearErrorSummary(form);
      saveDraft(form);

      if (!form.checkValidity()) {
        event.preventDefault();
        showErrorSummary(form);
        return;
      }

      const timestamp = form.querySelector("[data-ecpr-submission-timestamp]");
      if (timestamp) {
        timestamp.value = new Date().toISOString();
        timestamp.disabled = false;
      }
      savePendingLead(form);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeProjectReview, { once: true });
  } else {
    initializeProjectReview();
  }
})();
